'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  useArchiveProjectMutation,
  useLeaveProjectMutation,
  useProject,
  useRestoreProjectMutation,
  useTeams,
  useTransferProjectMutation,
  useUpdateProjectMutation,
} from '../hooks/queries/useTeams';
import { queryKeys } from '../hooks/queries/queryKeys';
import {
  deleteProject,
  fetchProjectMembers,
  fetchTeamMembers,
  type Project,
  type ProjectMember,
  type TeamMember,
} from '../lib/api';
import { getAuthSession } from '../lib/authSession';
import { resolveApiErrorToastMessage } from '../lib/api/localizeApiError';
import { canManageTeamMembers, findCurrentTeamMembership } from '../lib/permissions';
import { clearWorkspaceContext, getWorkspaceContext } from '../lib/workspaceContext';
import { Spinner } from './loading/Spinner';
import { BaseModal } from './BaseModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import { Select, type SelectOption } from './Select';
import { CollapsibleSection } from './CollapsibleSection';

const languageOptionValues = ['en', 'ko', 'ja', 'zh-CN', 'zh-TW', 'es', 'fr', 'de', 'pt-BR', 'vi', 'id', 'th'] as const;
const languageAliases: Record<string, string> = {
  English: 'en',
  Korean: 'ko',
  한국어: 'ko',
  Japanese: 'ja',
  日本語: 'ja',
  'Simplified Chinese': 'zh-CN',
  简体中文: 'zh-CN',
  'Traditional Chinese': 'zh-TW',
  繁體中文: 'zh-TW',
  Spanish: 'es',
  Español: 'es',
  French: 'fr',
  Français: 'fr',
  German: 'de',
  Deutsch: 'de',
  'Brazilian Portuguese': 'pt-BR',
  'Português do Brasil': 'pt-BR',
  Vietnamese: 'vi',
  'Tiếng Việt': 'vi',
  Indonesian: 'id',
  'Bahasa Indonesia': 'id',
  Thai: 'th',
  ไทย: 'th',
};

const resolveLanguageOption = (language: string): string | null => {
  if (languageOptionValues.some((value) => value === language)) {
    return language;
  }
  return languageAliases[language] ?? null;
};

type ProjectSettingsViewProps = {
  projectId: string;
  // 기본 설정 섹션과 이관 섹션 사이에 끼워 넣을 추가 콘텐츠(예: 태그). 미지정 시 아무것도 렌더하지 않는다.
  afterGeneral?: ReactNode;
  // 프로젝트 삭제가 성공하면 호출된다. 부모가 모달 닫기/홈 이동 등 후처리를 수행한다.
  onProjectDeleted?: (projectId: string) => void;
};

export default function ProjectSettingsView({ projectId, afterGeneral, onProjectDeleted }: ProjectSettingsViewProps) {
  const t = useTranslations('projects.settings');
  const tCommon = useTranslations('common');
  const tWorkspace = useTranslations('workspace.page');
  const tRoot = useTranslations();
  const queryClient = useQueryClient();
  const session = useMemo(() => getAuthSession()!, []);
  const projectQuery = useProject(session.token, projectId);
  const { data: teams = [] } = useTeams(session.token);
  const updateProjectMutation = useUpdateProjectMutation(session.token);
  const transferProjectMutation = useTransferProjectMutation(session.token);
  const leaveProjectMutation = useLeaveProjectMutation(session.token);
  const archiveProjectMutation = useArchiveProjectMutation(session.token);
  const restoreProjectMutation = useRestoreProjectMutation(session.token);

  const [projectName, setProjectName] = useState('');
  const [visibility, setVisibility] = useState<Project['visibility']>('PRIVATE');
  const [languageOption, setLanguageOption] = useState('en');
  const [customLanguage, setCustomLanguage] = useState('');
  const [isCustomLanguage, setIsCustomLanguage] = useState(false);
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isPermissionLoading, setIsPermissionLoading] = useState(true);
  const [transferTargetTeamId, setTransferTargetTeamId] = useState('');
  const [leaveConfirmDraft, setLeaveConfirmDraft] = useState('');
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfirmDraft, setDeleteConfirmDraft] = useState('');
  const [isDeletingProject, setIsDeletingProject] = useState(false);

  useEffect(() => {
    if (projectQuery.data) {
      setProjectName(projectQuery.data.name);
      setVisibility(projectQuery.data.visibility);
      const projectLanguage = projectQuery.data.language.trim() || 'en';
      const matchedOption = resolveLanguageOption(projectLanguage);
      if (matchedOption) {
        setLanguageOption(matchedOption);
        setCustomLanguage('');
        setIsCustomLanguage(false);
      } else {
        setCustomLanguage(projectLanguage);
        setIsCustomLanguage(true);
      }
    }
  }, [projectQuery.data]);

  useEffect(() => {
    if (!projectQuery.data) return;
    const fallbackTeam = teams.find((team) => team.id !== projectQuery.data.teamId);
    setTransferTargetTeamId(fallbackTeam?.id ?? '');
  }, [projectQuery.data, teams]);

  const languageOptions = useMemo<SelectOption[]>(
    () => [
      { value: 'en', label: t('languageEnglish') },
      { value: 'ko', label: t('languageKorean') },
      { value: 'ja', label: t('languageJapanese') },
      { value: 'zh-CN', label: t('languageSimplifiedChinese') },
      { value: 'zh-TW', label: t('languageTraditionalChinese') },
      { value: 'es', label: t('languageSpanish') },
      { value: 'fr', label: t('languageFrench') },
      { value: 'de', label: t('languageGerman') },
      { value: 'pt-BR', label: t('languageBrazilianPortuguese') },
      { value: 'vi', label: t('languageVietnamese') },
      { value: 'id', label: t('languageIndonesian') },
      { value: 'th', label: t('languageThai') },
    ],
    [t],
  );

  useEffect(() => {
    if (!projectId) {
      toast.error(t('toastProjectIdMissing'));
      setIsPermissionLoading(false);
      return;
    }

    if (!projectQuery.data) {
      return;
    }

    const loadPermissions = async () => {
      setIsPermissionLoading(true);
      try {
        const [foundProjectMembers, foundTeamMembers] = await Promise.all([
          fetchProjectMembers(session.token, projectId),
          fetchTeamMembers(session.token, projectQuery.data.teamId),
        ]);
        setProjectMembers(foundProjectMembers);
        setTeamMembers(foundTeamMembers);
      } catch (loadError) {
        const message = resolveApiErrorToastMessage(tRoot, loadError);
        if (message) toast.error(message);
      } finally {
        setIsPermissionLoading(false);
      }
    };

    void loadPermissions();
  }, [projectId, projectQuery.data, session.token, t, tRoot]);

  const currentTeamMembership = useMemo(() => {
    return findCurrentTeamMembership(teamMembers, session.member.id);
  }, [session.member.id, teamMembers]);

  const currentProjectMembership = useMemo(() => {
    return projectMembers.find((member) => member.memberId === session.member.id);
  }, [projectMembers, session.member.id]);

  const canManage =
    canManageTeamMembers(currentTeamMembership?.role) || canManageTeamMembers(currentProjectMembership?.role);
  const isLoading = projectQuery.isLoading || isPermissionLoading;
  const savedLanguage = projectQuery.data?.language ?? 'en';
  const savedProjectName = projectQuery.data?.name ?? '';
  const savedVisibility = projectQuery.data?.visibility ?? 'PRIVATE';
  const nextProjectName = projectName.trim();
  const nextLanguage = isCustomLanguage ? customLanguage.trim() : languageOption;
  const hasChanges =
    (nextProjectName.length > 0 && nextProjectName !== savedProjectName) ||
    (nextLanguage.length > 0 && nextLanguage !== savedLanguage) ||
    visibility !== savedVisibility;

  const visibilityOptions = useMemo<SelectOption[]>(
    () => [
      { value: 'TEAM', label: t('visibilityTeam') },
      { value: 'PRIVATE', label: t('visibilityPrivate') },
    ],
    [t],
  );
  const canTransfer =
    canManage && Boolean(projectQuery.data) && teams.some((team) => team.id !== projectQuery.data?.teamId);
  const isProjectBusy =
    updateProjectMutation.isPending ||
    transferProjectMutation.isPending ||
    leaveProjectMutation.isPending ||
    archiveProjectMutation.isPending ||
    restoreProjectMutation.isPending ||
    isDeletingProject;

  const handleSave = async () => {
    if (!projectQuery.data) {
      return;
    }

    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    if (!nextLanguage) {
      toast.error(t('toastLanguageRequired'));
      return;
    }

    if (!nextProjectName) {
      toast.error(t('toastProjectNameRequired'));
      return;
    }

    try {
      const updated = await updateProjectMutation.mutateAsync({
        projectId,
        teamId: projectQuery.data.teamId,
        input: { name: nextProjectName, language: nextLanguage, visibility },
      });
      setProjectName(updated.name);
      setVisibility(updated.visibility);
      const updatedLanguage = updated.language.trim() || 'en';
      const matchedOption = resolveLanguageOption(updatedLanguage);
      if (matchedOption) {
        setLanguageOption(matchedOption);
        setCustomLanguage('');
        setIsCustomLanguage(false);
      } else {
        setCustomLanguage(updatedLanguage);
        setIsCustomLanguage(true);
      }
      toast.success(t('toastSaved'));
    } catch (saveError) {
      const message = resolveApiErrorToastMessage(tRoot, saveError);
      if (message) toast.error(message);
    }
  };

  const handleTransferProject = async () => {
    if (!projectQuery.data || !transferTargetTeamId || transferTargetTeamId === projectQuery.data.teamId) return;
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    try {
      await transferProjectMutation.mutateAsync({
        projectId,
        sourceTeamId: projectQuery.data.teamId,
        targetTeamId: transferTargetTeamId,
      });
      const saved = getWorkspaceContext();
      if (saved?.projectId === projectId) {
        clearWorkspaceContext();
      }
      toast.success(tWorkspace('projectTransferred'));
    } catch (transferError) {
      const message = resolveApiErrorToastMessage(tRoot, transferError);
      if (message) toast.error(message);
    }
  };

  const handleArchiveToggle = async () => {
    if (!projectQuery.data) return;
    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    try {
      if (projectQuery.data.archivedAt) {
        await restoreProjectMutation.mutateAsync({ projectId, teamId: projectQuery.data.teamId });
        toast.success(tWorkspace('projectRestored'));
      } else {
        await archiveProjectMutation.mutateAsync({ projectId, teamId: projectQuery.data.teamId });
        toast.success(tWorkspace('projectArchived'));
      }
    } catch (archiveError) {
      const message = resolveApiErrorToastMessage(tRoot, archiveError);
      if (message) toast.error(message);
    }
  };

  const handleLeaveProject = async () => {
    if (!projectQuery.data || leaveConfirmDraft !== projectQuery.data.name) return;

    try {
      await leaveProjectMutation.mutateAsync({ projectId, teamId: projectQuery.data.teamId });
      const saved = getWorkspaceContext();
      if (saved?.projectId === projectId) {
        clearWorkspaceContext();
      }
      setIsLeaveModalOpen(false);
      setLeaveConfirmDraft('');
      toast.success(tWorkspace('projectLeft'));
    } catch (leaveError) {
      const message = resolveApiErrorToastMessage(tRoot, leaveError);
      if (message) toast.error(message);
    }
  };

  const handleDeleteProject = async () => {
    if (!projectQuery.data) return;
    // 입력한 프로젝트 이름이 실제 이름과 정확히 일치할 때만 삭제를 진행한다.
    if (deleteConfirmDraft !== projectQuery.data.name) return;
    setIsDeletingProject(true);
    try {
      await deleteProject(session.token, projectId);
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects.byTeam(projectQuery.data.teamId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.me.projects });
      const saved = getWorkspaceContext();
      if (saved?.projectId === projectId) {
        clearWorkspaceContext();
      }
      setIsDeleteModalOpen(false);
      setDeleteConfirmDraft('');
      toast.success(t('toastProjectDeleted'));
      onProjectDeleted?.(projectId);
    } catch (deleteError) {
      const message = resolveApiErrorToastMessage(tRoot, deleteError);
      if (message) toast.error(message);
    } finally {
      setIsDeletingProject(false);
    }
  };

  if (projectQuery.isError) {
    return <div className="rounded-xl border border-error/30 bg-error/5 p-4 text-sm text-error">{t('loadFailed')}</div>;
  }

  return (
    <div className="space-y-4">
      {!isLoading && !canManage ? <p className="text-xs text-warning">{t('permissionHint')}</p> : null}

      <section className="rounded-2xl border border-base-300 bg-base-100 p-4 shadow-xs sm:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-base-content">{t('sectionTitle')}</h3>
            <p className="mt-1 text-xs leading-5 text-[var(--color-fg-muted)]">{t('sectionDescription')}</p>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={isLoading || !canManage || !hasChanges || updateProjectMutation.isPending}
            onClick={() => void handleSave()}
          >
            {updateProjectMutation.isPending ? t('saving') : t('save')}
          </button>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            <div className="skeleton h-8 w-full rounded-lg" />
            <div className="skeleton h-12 w-full rounded-xl" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
            <div className="space-y-2">
              <label htmlFor="project-name" className="block text-sm font-medium text-base-content">
                {tWorkspace('projectName')}
              </label>
              <input
                id="project-name"
                type="text"
                value={projectName}
                onChange={(event) => setProjectName(event.target.value)}
                disabled={!canManage || updateProjectMutation.isPending}
                className="input input-bordered input-sm w-full"
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor={isCustomLanguage ? 'project-custom-language' : 'project-language'}
                className="block text-sm font-medium text-base-content"
              >
                {t('languageLabel')}
              </label>
              <div className="space-y-2">
                {isCustomLanguage ? (
                  <>
                    <input
                      id="project-custom-language"
                      type="text"
                      value={customLanguage}
                      onChange={(event) => setCustomLanguage(event.target.value)}
                      placeholder={t('customLanguagePlaceholder')}
                      maxLength={64}
                      disabled={!canManage || updateProjectMutation.isPending}
                      className="input input-bordered input-sm w-full"
                    />
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs h-auto min-h-0 px-0 py-0 text-xs font-normal text-primary hover:bg-transparent"
                      disabled={!canManage || updateProjectMutation.isPending}
                      onClick={() => {
                        setIsCustomLanguage(false);
                        setCustomLanguage('');
                      }}
                    >
                      {t('useLanguageList')}
                    </button>
                    <p className="text-xs leading-5 text-[var(--color-fg-muted)]">{t('customLanguageHint')}</p>
                  </>
                ) : (
                  <>
                    <Select
                      id="project-language"
                      options={languageOptions}
                      value={languageOption}
                      onChange={setLanguageOption}
                      size="sm"
                      disabled={!canManage || updateProjectMutation.isPending}
                      fullWidth
                    />
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs h-auto min-h-0 px-0 py-0 text-xs font-normal text-primary hover:bg-transparent"
                      disabled={!canManage || updateProjectMutation.isPending}
                      onClick={() => {
                        setIsCustomLanguage(true);
                        setCustomLanguage('');
                      }}
                    >
                      {t('useCustomLanguage')}
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="project-visibility" className="block text-sm font-medium text-base-content">
                {t('visibilityLabel')}
              </label>
              <Select
                id="project-visibility"
                options={visibilityOptions}
                value={visibility}
                onChange={(value) => setVisibility(value as Project['visibility'])}
                size="sm"
                disabled={!canManage || updateProjectMutation.isPending}
                fullWidth
              />
              <p className="text-xs leading-5 text-[var(--color-fg-muted)]">{t('visibilityDescription')}</p>
            </div>
          </div>
        )}
      </section>

      {afterGeneral}

      <CollapsibleSection
        title={t('transferSectionTitle')}
        description={tWorkspace('transferProjectDescription', { name: projectName || tWorkspace('unnamedProject') })}
      >
        <div className="flex max-w-sm flex-col gap-2 sm:flex-row">
          <Select
            id="project-transfer-target-team"
            value={transferTargetTeamId}
            onChange={setTransferTargetTeamId}
            options={[
              { value: '', label: tWorkspace('selectTargetTeam'), disabled: true },
              ...teams
                .filter((team) => team.id !== projectQuery.data?.teamId)
                .map((team) => ({ value: team.id, label: team.name })),
            ]}
            size="sm"
            fullWidth
            disabled={!canTransfer || transferProjectMutation.isPending}
          />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!canTransfer || !transferTargetTeamId || transferProjectMutation.isPending}
            onClick={() => void handleTransferProject()}
          >
            {transferProjectMutation.isPending ? <Spinner /> : null}
            {tWorkspace('transfer')}
          </button>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title={tWorkspace('dangerZone')} description={t('dangerDescription')} tone="error">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-warning btn-sm"
            disabled={isProjectBusy}
            onClick={() => setIsLeaveModalOpen(true)}
          >
            {tWorkspace('leaveProject')}
          </button>
          <button
            type="button"
            className="btn btn-warning btn-sm"
            disabled={!canManage || isProjectBusy}
            onClick={() => setIsArchiveModalOpen(true)}
          >
            {archiveProjectMutation.isPending || restoreProjectMutation.isPending ? <Spinner /> : null}
            {projectQuery.data?.archivedAt ? tWorkspace('restoreProject') : tWorkspace('archiveProject')}
          </button>
          <button
            type="button"
            className="btn btn-error btn-sm"
            disabled={!canManage || isProjectBusy}
            onClick={() => setIsDeleteModalOpen(true)}
          >
            {tCommon('actions.delete')}
          </button>
        </div>
      </CollapsibleSection>

      <BaseModal
        isOpen={isLeaveModalOpen}
        onClose={() => {
          setIsLeaveModalOpen(false);
          setLeaveConfirmDraft('');
        }}
        variant="bottomSheet"
        size="md"
      >
        <div>
          <h3 className="text-lg font-bold">{tWorkspace('leaveProject')}</h3>
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
            {tWorkspace('leaveProjectDescription', { name: projectName || tWorkspace('unnamedProject') })}
          </p>
          <div className="mt-6">
            <label
              className="mb-1.5 block text-xs font-medium text-[var(--color-fg-muted)]"
              htmlFor="project-leave-confirm"
            >
              {tWorkspace('confirmByTyping', { name: projectName || tWorkspace('unnamedProject') })}
            </label>
            <input
              id="project-leave-confirm"
              className="input input-bordered w-full"
              value={leaveConfirmDraft}
              onChange={(event) => setLeaveConfirmDraft(event.target.value)}
            />
          </div>
          <div className="modal-action">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setIsLeaveModalOpen(false);
                setLeaveConfirmDraft('');
              }}
            >
              {tCommon('actions.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-warning btn-sm"
              disabled={leaveConfirmDraft !== projectName || leaveProjectMutation.isPending}
              onClick={() => void handleLeaveProject()}
            >
              {leaveProjectMutation.isPending ? <Spinner /> : null}
              {tWorkspace('leave')}
            </button>
          </div>
        </div>
      </BaseModal>

      <DeleteConfirmModal
        isOpen={isArchiveModalOpen}
        title={projectQuery.data?.archivedAt ? tWorkspace('restoreProject') : tWorkspace('archiveProject')}
        message={
          projectQuery.data?.archivedAt
            ? t('confirmRestoreProject', { name: projectName || tWorkspace('unnamedProject') })
            : t('confirmArchiveProject', { name: projectName || tWorkspace('unnamedProject') })
        }
        confirmLabel={projectQuery.data?.archivedAt ? tWorkspace('restoreProject') : tWorkspace('archiveProject')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={archiveProjectMutation.isPending || restoreProjectMutation.isPending}
        onConfirm={() => void handleArchiveToggle().then(() => setIsArchiveModalOpen(false))}
        onCancel={() => {
          if (!archiveProjectMutation.isPending && !restoreProjectMutation.isPending) {
            setIsArchiveModalOpen(false);
          }
        }}
      />

      <BaseModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isDeletingProject) {
            setIsDeleteModalOpen(false);
            setDeleteConfirmDraft('');
          }
        }}
        variant="bottomSheet"
        size="md"
      >
        <div>
          <h3 className="text-lg font-bold">{tCommon('actions.delete')}</h3>
          <p className="mt-1 whitespace-pre-line text-sm text-[var(--color-fg-muted)]">
            {tWorkspace('confirmDeleteProject', { name: projectName || tWorkspace('unnamedProject') })}
          </p>
          <p className="mt-2 text-sm font-medium text-error">{tWorkspace('deleteProjectIrreversible')}</p>
          <div className="mt-6">
            <label
              className="mb-1.5 block text-xs font-medium text-[var(--color-fg-muted)]"
              htmlFor="project-delete-confirm"
            >
              {tWorkspace('confirmByTyping', { name: projectName || tWorkspace('unnamedProject') })}
            </label>
            <input
              id="project-delete-confirm"
              className="input input-bordered w-full"
              value={deleteConfirmDraft}
              onChange={(event) => setDeleteConfirmDraft(event.target.value)}
              disabled={isDeletingProject}
            />
          </div>
          <div className="modal-action">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={isDeletingProject}
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeleteConfirmDraft('');
              }}
            >
              {tCommon('actions.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-error btn-sm"
              disabled={deleteConfirmDraft !== projectName || isDeletingProject}
              onClick={() => void handleDeleteProject()}
            >
              {isDeletingProject ? <Spinner /> : null}
              {tCommon('actions.delete')}
            </button>
          </div>
        </div>
      </BaseModal>
    </div>
  );
}
